import { useState, useEffect } from 'react';
import { User, AuditLog } from '../types';
import { useAuditLog } from '../hooks/useAuditLog';
import { Download, Search, RefreshCw, X, Activity } from 'lucide-react';
import './AuditLogViewer.css';

interface AuditLogViewerProps {
  currentUser: User;
  onClose: () => void;
}

export function AuditLogViewer({ currentUser, onClose }: AuditLogViewerProps) {
  const { getRecentLogs } = useAuditLog();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    user: '',
    dateFrom: '',
    dateTo: ''
  });

  // Load audit logs
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const recentLogs = await getRecentLogs(1000); // Get more logs
      setLogs(recentLogs);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  // Filter logs based on current filters
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !filters.search || 
      log.userEmail?.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.action.toLowerCase().includes(filters.search.toLowerCase()) ||
      JSON.stringify(log.details).toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesAction = !filters.action || log.action === filters.action;
    const matchesUser = !filters.user || log.userEmail === filters.user;
    
    const logDate = new Date(log.timestamp);
    const fromDate = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const toDate = filters.dateTo ? new Date(filters.dateTo) : null;
    
    const matchesDateFrom = !fromDate || logDate >= fromDate;
    const matchesDateTo = !toDate || logDate <= toDate;
    
    return matchesSearch && matchesAction && matchesUser && matchesDateFrom && matchesDateTo;
  });

  // Get unique actions for filter dropdown
  const uniqueActions = [...new Set(logs.map(log => log.action))].sort();
  const uniqueUsers = [...new Set(logs.map(log => log.userEmail).filter(Boolean))].sort();

  // Export logs to CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Details', 'Session ID', 'IP Address'];
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        log.timestamp.toISOString(),
        log.userEmail ?? '',
        log.action,
        JSON.stringify(log.details).replace(/"/g, '""'),
        log.sessionId ?? '',
        log.ipAddress || 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'login':
        return '#38a169';
      case 'logout':
        return '#e53e3e';
      case 'document_view':
        return '#3182ce';
      case 'document_download':
        return '#805ad5';
      case 'search':
        return '#d69e2e';
      case 'bookmark_add':
        return '#38a169';
      case 'bookmark_remove':
        return '#e53e3e';
      case 'session_timeout':
        return '#dd6b20';
      default:
        return '#718096';
    }
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Human-readable one-line summary of the log details
  const summarizeDetails = (log: AuditLog): string => {
    const d = log.details || {};
    switch (log.action) {
      case 'login':
        return 'Signed in';
      case 'logout':
        return 'Signed out';
      case 'session_timeout':
        return 'Session timed out';
      case 'document_view':
        return d.documentTitle || '';
      case 'file_load_local':
      case 'file_load_sharepoint':
        return `${d.fileCount ?? '?'} files, ${d.sequenceCount ?? '?'} sequence(s)${d.applicationId ? ` — ${d.applicationId}` : ''}`;
      case 'sequence_change':
        return d.sequenceId ? `Sequence ${d.sequenceId}` : '';
      case 'bookmark_add':
      case 'bookmark_remove':
        return d.documentTitle || '';
      case 'annotation_add':
        return `${d.documentTitle || ''} (p.${d.page ?? '?'}): ${d.textPreview || ''}`;
      case 'annotation_resolve':
      case 'annotation_reopen':
      case 'annotation_delete':
        return `${d.documentTitle || ''} (p.${d.page ?? '?'})${d.author ? ` — comment by ${d.author}` : ''}`;
      case 'review_signoff': {
        const meaningLabel =
          d.meaning === 'approved' ? 'Approved' : d.meaning === 'in_review' ? 'In review' : 'Review completed';
        return `Sequence ${d.sequenceId ?? '?'} — ${meaningLabel}${d.comment ? `: "${d.comment}"` : ''} (password re-authenticated)`;
      }
      case 'comments_export':
        return `${d.commentCount ?? '?'} comments (${d.openCount ?? '?'} open) — ${d.applicationId || ''}`;
      case 'search':
        return d.query ? `"${d.query}"` : '';
      case 'theme_change':
        return d.newTheme ? `Switched to ${d.newTheme}` : '';
      case 'dashboard_reset':
        return 'Dashboard reset';
      default:
        return Object.entries(d)
          .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
    }
  };

  if (currentUser.role !== 'admin') {
    return (
      <div className="audit-log-viewer">
        <div className="access-denied">
          <X className="icon" />
          <h3>Access Denied</h3>
          <p>Only administrators can view audit logs.</p>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-log-viewer">
      <div>
        <div className="audit-log-header">
        <h2>Audit Logs</h2>
        <div className="header-actions">
          <button onClick={loadLogs} className="refresh-button" disabled={loading}>
            <RefreshCw className={`icon ${loading ? 'spinning' : ''}`} />
            Refresh
          </button>
          <button onClick={exportToCSV} className="export-button">
            <Download className="icon" />
            Export CSV
          </button>
          <button onClick={onClose} className="close-button">
            <X className="icon" />
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <label>Search</label>
          <div className="search-input">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search logs..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
        </div>

        <div className="filter-group">
          <label>Action</label>
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          >
            <option value="">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>
                {formatAction(action)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>User</label>
          <select
            value={filters.user}
            onChange={(e) => setFilters({ ...filters, user: e.target.value })}
          >
            <option value="">All Users</option>
            {uniqueUsers.map(user => (
              <option key={user} value={user}>
                {user}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>From Date</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>To Date</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          />
        </div>
      </div>

      <div className="logs-content">
        <div className="logs-header">
          <h3>Audit Logs ({filteredLogs.length} of {logs.length})</h3>
        </div>

        {loading ? (
          <div className="loading">Loading audit logs...</div>
        ) : (
          <div className="logs-table-wrapper">
            {filteredLogs.length === 0 ? (
              <div className="no-logs">
                <Activity className="icon" />
                <p>No audit logs found matching your filters.</p>
              </div>
            ) : (
              <table className="logs-table">
                <thead>
                  <tr>
                    <th className="col-time">Date &amp; Time</th>
                    <th className="col-user">User</th>
                    <th className="col-action">Activity</th>
                    <th className="col-details">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="log-row">
                      <td className="col-time">{log.timestamp.toLocaleString()}</td>
                      <td className="col-user">{log.userEmail ?? 'Unknown user'}</td>
                      <td className="col-action">
                        <span
                          className="action-badge"
                          style={{ color: getActionColor(log.action), borderColor: getActionColor(log.action) }}
                        >
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td className="col-details" title={summarizeDetails(log)}>
                        {summarizeDetails(log)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
